<template>
  <v-parallax class="hero" src="/img/hero-pregnancy.webp" alt="Pregnancy massage therapy">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You deserve calm, supported care</div>
        <div class="text-center text-h2 pa-2">nurture body and baby</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PregnancyMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our pregnancy massage?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PregnancyMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by Our Clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text">"{{ review.quote }}"</p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PregnancyMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Pregnancy Massage?"
    :benefits="benefits"
    :booking-url="$bookingUrls.PregnancyMassage.Default" />

  <section id="pricing">
    <div class="full-width-header">Pregnancy Massage Sessions</div>
    <div class="content pb-16">
      <p class="content-intro">
        Gentle, supportive care designed for every trimester. Choose the session length that feels best for you.
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="Packages"
          :booking-options="packageOptions"
          :link-target="'_self'"
          class="service-item-card" />
          
        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel more comfortable?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Ease tension, improve circulation, and enjoy a calm moment just for you. Book your pregnancy massage today.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PregnancyMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'PregnancyMassageView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Pregnancy-Safe Techniques',
          description: 'Positioning and pressure are tailored for comfort and safety throughout your pregnancy.'
        },
        {
          title: 'Relief Where You Need It',
          description: 'Focused care for lower back, hips, shoulders, and tired legs.'
        },
        {
          title: 'Calm, Restorative Space',
          description: 'A peaceful environment that helps you unwind, breathe, and recharge.'
        }
      ],
      starsImage: '/img/five-stars.png',
      featureReviews: [
        {
          name: 'Laura Payne',
          quote:
            'Jasmine is so professional and extremely knowledgable. Looking forward to my next appointment already'
        }
      ],
      benefits: [
        {
          title: 'Eases Back & Hip Pain',
          description: 'Gentle techniques relieve the strain that pregnancy places on your lower back, hips, and legs.'
        },
        {
          title: 'Reduces Swelling',
          description: 'Improves circulation and lymphatic flow, helping to manage pregnancy-related fluid retention in the legs and feet.'
        },
        {
          title: 'Promotes Better Sleep',
          description: 'Calms the body and mind, helping expectant mothers rest more comfortably through each trimester.'
        }
      ],
      packageOptions: [
        new BookingOption('5 x 50 min', BookingUrls.PregnancyMassage.Package50)
      ],
      sessionOptions: [
        new BookingOption('50 min', BookingUrls.PregnancyMassage.Min50)
      ]
    };
  }
});
</script>
